import { useState } from 'react';
import { motion } from 'framer-motion';

export default function QuoteRequest() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [details, setDetails] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setIsError(false);

    const form = e.currentTarget;
    const formData = new FormData(form);
    const encoded = new URLSearchParams();
    formData.forEach((value, key) => {
      encoded.append(key, value as string);
    });

    try {
      const res = await fetch('https://readdy.ai/api/form/d709q132m2156g4s6pig', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encoded.toString(),
      });

      if (res.ok) {
        setIsSuccess(true);
        form.reset();
        setDetails('');
        setTimeout(() => setIsSuccess(false), 4000);
      } else {
        setIsError(true);
      }
    } catch {
      setIsError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-24 bg-dark-800 relative overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-electric-blue/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-electric-cyan/10 rounded-full blur-3xl" />
      </div>

      <div className="max-w-4xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="font-orbitron text-5xl font-bold text-white mb-4">
            REQUEST A <span className="text-electric-blue">QUOTE</span>
          </h2>
          <p className="font-inter text-white/60 text-lg">
            Get a free, no-obligation quote for your transportation needs
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-8"
        >
          {isSuccess && (
            <div className="mb-6 flex items-center gap-3 bg-green-500/20 border border-green-500/40 rounded-xl px-5 py-4">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-checkbox-circle-fill text-green-400 text-xl"></i>
              </div>
              <p className="font-inter text-green-300 text-sm">
                Your quote request was sent successfully! We&apos;ll be in touch shortly.
              </p>
            </div>
          )}

          {isError && (
            <div className="mb-6 flex items-center gap-3 bg-red-500/20 border border-red-500/40 rounded-xl px-5 py-4">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-error-warning-fill text-red-400 text-xl"></i>
              </div>
              <p className="font-inter text-red-300 text-sm">
                Something went wrong. Please try again or call us at (415) 888-5336.
              </p>
            </div>
          )}

          <form
            onSubmit={handleSubmit}
            data-readdy-form
            className="space-y-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block font-inter text-white/80 text-sm mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-white font-inter text-sm focus:outline-none focus:border-electric-blue transition-colors duration-300"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block font-inter text-white/80 text-sm mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  name="phone"
                  required
                  className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-white font-inter text-sm focus:outline-none focus:border-electric-blue transition-colors duration-300"
                  placeholder="(702) 555-1234"
                />
              </div>

              <div>
                <label className="block font-inter text-white/80 text-sm mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-white font-inter text-sm focus:outline-none focus:border-electric-blue transition-colors duration-300"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label className="block font-inter text-white/80 text-sm mb-2">
                  Service Type *
                </label>
                <select
                  name="serviceType"
                  required
                  className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-white font-inter text-sm focus:outline-none focus:border-electric-blue transition-colors duration-300 cursor-pointer"
                >
                  <option value="">Select a service</option>
                  <option value="Residential Relocation">Residential Relocation</option>
                  <option value="Commercial Interstate">Commercial Interstate</option>
                  <option value="Intrastate Relocations">Intrastate Relocations</option>
                  <option value="General Freight">General Freight</option>
                  <option value="Building Materials">Building Materials</option>
                  <option value="Vehicle Transport">Vehicle Transport</option>
                </select>
              </div>

              <div>
                <label className="block font-inter text-white/80 text-sm mb-2">
                  Origin Location *
                </label>
                <input
                  type="text"
                  name="origin"
                  required
                  className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-white font-inter text-sm focus:outline-none focus:border-electric-blue transition-colors duration-300"
                  placeholder="Las Vegas, NV"
                />
              </div>

              <div>
                <label className="block font-inter text-white/80 text-sm mb-2">
                  Destination Location *
                </label>
                <input
                  type="text"
                  name="destination"
                  required
                  className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-white font-inter text-sm focus:outline-none focus:border-electric-blue transition-colors duration-300"
                  placeholder="San Francisco, CA"
                />
              </div>
            </div>

            <div>
              <label className="block font-inter text-white/80 text-sm mb-2">
                Preferred Date
              </label>
              <input
                type="date"
                name="date"
                className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-white font-inter text-sm focus:outline-none focus:border-electric-blue transition-colors duration-300 cursor-pointer"
              />
            </div>

            <div>
              <label className="block font-inter text-white/80 text-sm mb-2">
                Additional Details
                <span className="ml-2 text-white/40 text-xs">({details.length}/500)</span>
              </label>
              <textarea
                name="details"
                rows={4}
                maxLength={500}
                value={details}
                onChange={e => setDetails(e.target.value)}
                className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-white font-inter text-sm focus:outline-none focus:border-electric-blue transition-colors duration-300 resize-none"
                placeholder="Tell us more about your transportation needs..."
              />
              {details.length >= 500 && (
                <p className="text-yellow-400 text-xs mt-1 font-inter">Maximum 500 characters reached.</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting || isSuccess}
              className="w-full py-4 bg-gradient-to-r from-electric-blue to-electric-cyan text-dark-900 font-orbitron font-bold text-base rounded-full hover:opacity-90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-loader-4-line animate-spin text-xl"></i>
                  </div>
                  <span>SUBMITTING...</span>
                </span>
              ) : isSuccess ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-checkbox-circle-fill text-xl"></i>
                  </div>
                  <span>QUOTE REQUEST SENT!</span>
                </span>
              ) : (
                'GET FREE QUOTE'
              )}
            </button>
          </form>
        </motion.div>
      </div>
    </section>
  );
}
